import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { PERMS } from "@/components/Can";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, KeyRound, Trash2, ShieldCheck, Ban, CheckCircle2 } from "lucide-react";

const EMPTY = { name: "", email: "", password: "", permissions: [], location: "" };

const LocationSelect = ({ value, onChange, locations, testid }) => (
  <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-8 px-2 border border-slate-300 rounded text-xs bg-white outline-none focus:border-blue-600 max-w-[160px]">
    <option value="">— No home location —</option>
    {locations.map(l => <option key={l} value={l}>{l}</option>)}
    {value && !locations.includes(value) && <option value={value}>{value}</option>}
  </select>
);

export default function TeamSettings() {
  const { user: me } = useAuth();
  const [members, setMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/team").then(r => setMembers(r.data));
  useEffect(() => { load(); api.get("/company").then(r => setLocations(r.data.locations || [])); }, []);
  const setLocation = async (m, location) => {
    try { await api.put(`/team/${m.user_id}/location`, { location }); toast.success(location ? `${m.name || m.email} → ${location}` : "Home location cleared"); load(); }
    catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
  };

  const toggle = (list, p) => list.includes(p) ? list.filter(x => x !== p) : [...list, p];
  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/team", form); toast.success(`${form.name} added`); setForm(EMPTY); load(); }
    catch (err) { toast.error(fmtError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const update = async (m, patch) => {
    try { await api.put(`/team/${m.user_id}`, { name: m.name, permissions: m.permissions, disabled: m.disabled, location: m.location || "", ...patch }); load(); }
    catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
  };
  const resetPw = async (m) => {
    const pw = window.prompt(`New password for ${m.email} (min 6 chars):`);
    if (!pw) return;
    try { await api.post(`/team/${m.user_id}/password`, { password: pw }); toast.success("Password updated"); }
    catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
  };
  const del = async (m) => {
    if (!window.confirm(`Remove ${m.email} from the team?`)) return;
    await api.delete(`/team/${m.user_id}`); toast.success("Member removed"); load();
  };

  return (
    <div className="space-y-4" data-testid="team-settings">
      <div><h2 className="text-lg font-semibold" style={{fontFamily:'Plus Jakarta Sans'}}>Team & Access Control</h2>
        <p className="text-xs text-slate-500">Members can always view data. Grant extra abilities per member. Only the administrator can manage the team.</p></div>

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="team-add-form">
        <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-2"><UserPlus size={14}/> Add member</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {[["name","Name","text"],["email","Email","email"],["password","Password (min 6)","text"]].map(([k,l,t]) => (
            <div key={k}><label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{l}*</label>
              <input data-testid={`team-field-${k}`} type={t} required minLength={k==="password"?6:undefined} value={form[k]} onChange={(e)=>setForm({...form,[k]:e.target.value})} autoComplete="off" className="mt-1 w-full h-9 px-2.5 border border-slate-300 rounded text-sm outline-none focus:border-blue-600"/></div>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
          {PERMS.map(([p,l,d]) => (
            <label key={p} className="flex items-center gap-1.5 text-xs" title={d}><input data-testid={`team-new-perm-${p}`} type="checkbox" checked={form.permissions.includes(p)} onChange={()=>setForm({...form,permissions:toggle(form.permissions,p)})}/>{l}</label>
          ))}
          <span className="text-xs text-slate-500 ml-auto flex items-center gap-2">Home location <LocationSelect testid="team-new-location" value={form.location} onChange={(v)=>setForm({...form, location: v})} locations={locations}/></span>
        </div>
        <button type="submit" data-testid="team-add-btn" disabled={busy} className="h-9 px-4 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-md text-xs font-semibold uppercase tracking-wider">Add member</button>
      </form>

      <div className="bg-white border border-slate-200 rounded-md table-scroll">
        <table className="acct-table" data-testid="team-table">
          <thead><tr><th>Member</th><th>Role</th><th>Home Location</th>{PERMS.map(([p,l]) => <th key={p} className="text-center" title={l}>{l}</th>)}<th>Status</th><th></th></tr></thead>
          <tbody>
            {members.map(m => {
              const admin = m.role === "admin";
              return (
                <tr key={m.user_id} data-testid={`team-row-${m.user_id}`} className={m.disabled ? "opacity-60" : ""}>
                  <td><div className="font-medium">{m.name || "—"}{m.user_id === me?.user_id && <span className="ml-1 text-[10px] text-slate-400">(you)</span>}</div><div className="text-xs text-slate-500 mono">{m.email}</div></td>
                  <td>{admin ? <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-900 text-white inline-flex items-center gap-1"><ShieldCheck size={11}/>Admin</span> : <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 border border-slate-200">Member</span>}</td>
                  <td><LocationSelect testid={`team-location-${m.user_id}`} value={m.location} onChange={(v)=>setLocation(m, v)} locations={locations}/></td>
                  {PERMS.map(([p]) => (
                    <td key={p} className="text-center">
                      <input type="checkbox" data-testid={`team-perm-${m.user_id}-${p}`} disabled={admin} checked={admin || m.permissions.includes(p)} onChange={()=>update(m, { permissions: toggle(m.permissions, p) })}/>
                    </td>
                  ))}
                  <td>{m.disabled ? <span className="text-[11px] text-rose-700 font-semibold inline-flex items-center gap-1"><Ban size={11}/>Disabled</span> : <span className="text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={11}/>Active</span>}</td>
                  <td className="text-right whitespace-nowrap">
                    {!admin && <>
                      <button data-testid={`team-toggle-${m.user_id}`} onClick={()=>update(m, { disabled: !m.disabled })} title={m.disabled ? "Enable" : "Disable"} className="p-1.5 text-slate-500 hover:text-slate-900">{m.disabled ? <CheckCircle2 size={14}/> : <Ban size={14}/>}</button>
                      <button data-testid={`team-reset-${m.user_id}`} onClick={()=>resetPw(m)} title="Reset password" className="p-1.5 text-slate-500 hover:text-slate-900"><KeyRound size={14}/></button>
                      <button data-testid={`team-delete-${m.user_id}`} onClick={()=>del(m)} title="Remove" className="p-1.5 text-slate-500 hover:text-rose-700"><Trash2 size={14}/></button>
                    </>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
